import { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "../BundleBuilder.module.css";
import { ProductObj, SubStepType } from "../../Types";
import { Collapsible, FormLayout, Icon, Label, Tooltip } from "@shopify/polaris";
import { DeleteIcon, DiscountIcon, InfoIcon } from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import RHFRenderSelectedProduct from "../../../../components/RHFRenderSelectedProduct/RHFRenderSelectedProduct";
import { BundleTypes, nonEmptyOverride, validateFontSize } from "../../constants";
import RHFRenderSelectedCollections from "../../../../components/RHFRenderSelectedCollections/RHFRenderSelectedCollections";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import { buildResourcePickerQuery, isLilSweetTreatStore } from "../../../constants";
import { FormCheckbox, FormSelect, FormTextField } from "../../../../components/FormFields";
import { useFormContext } from "react-hook-form";

export const BBSubStepConditions = ({
    subStep,
    cIdx,
    pIdx,
    handleEditSubStep = () => {},
    isMobile = false,
    money_format,
    substep_rules_type,
}: {
    subStep: SubStepType;
    cIdx: number;
    pIdx: number;
    handleEditSubStep: <K extends keyof SubStepType>(idx: number, key: K | "delete", value?: any) => void;
    isMobile: boolean;
    money_format: string;
    substep_rules_type?: "no_rules" | "step_level" | "substep_level";
}) => {
    const [expandSubStep, setExpandSubStep] = useState(0);
    const [stepErrors, setStepErrors] = useState<{
        step_label: string;
        products: string;
        collections: string;
        product_count_limit: string;
    }>({
        step_label: "Label Has error",
        products: "Empty products",
        collections: "Empty collection",
        product_count_limit: "Empty product count",
    });
    const [hasInteracted, setHasInteracted] = useState(false);

    const { formState } = useFormContext();
    const hasSubStepFieldError = !!(formState.errors as any)?.steps?.[pIdx]?.subSteps?.[cIdx];

    useEffect(() => {
        if (hasInteracted) {
            setStepErrors((ps) => ({
                ...ps,
                step_label: nonEmptyOverride(subStep.step_label, "Category Label is required"),
                product_count_limit: validateFontSize(
                    subStep.product_count_limit,
                    subStep.subStep_condition_basis == "bundle_total" ? "Amount" : "Items count",
                    { min: 0, max: 10000000 },
                ),
            }));
        } else {
            setStepErrors((ps) => ({ ...ps, step_label: "", product_count_limit: "" }));
        }
    }, [hasInteracted]);

    useEffect(() => {
        if (hasSubStepFieldError) setHasInteracted(true);
    }, [hasSubStepFieldError, cIdx, pIdx]);

    return (
        <>
            <div
                key={pIdx + cIdx + 10}
                onClick={() => setExpandSubStep((ps) => (ps == cIdx ? -1 : cIdx))}
                className={styles.customCardLabelWrapper}
                style={{
                    marginBottom: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#ffffff",
                    padding: "16px",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                    <Icon source={DiscountIcon} />
                    <Label id="review_step">{`Category ${cIdx + 1} Conditions`}</Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandSubStep == cIdx ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor="pointer" />
                    </div>
                </div>
            </div>
            <Collapsible
                open={expandSubStep == cIdx}
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
                id=""
            >
                <div>
                    <FormLayout>
                        {/* Steps Conditions Starts */}
                        {!(substep_rules_type === "no_rules" || substep_rules_type === "step_level") && (
                            <div style={{ padding: "16px", borderRadius: "8px" }}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "2px",
                                    }}
                                >
                                    <Label id="step_condition">
                                        <span style={{ fontSize: 14, fontWeight: 500 }}>Category Condition</span>
                                    </Label>
                                </div>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                                        gap: "12px",
                                        marginBottom: "12px",
                                    }}
                                >
                                    <FormSelect
                                        name={`steps.${pIdx}.subSteps.${cIdx}.subStep_condition_basis`}
                                        label=""
                                        labelHidden
                                        options={[
                                            { value: "bundle_total", label: "Total Amount" },
                                            { value: "items_count", label: "Total Quantity" },
                                        ]}
                                        onChange={(val) => handleEditSubStep(cIdx, "subStep_condition_basis", val)}
                                        value={subStep.subStep_condition_basis || "items_count"}
                                    />
                                    <FormSelect
                                        name={`steps.${pIdx}.subSteps.${cIdx}.subStep_condition_type`}
                                        label=""
                                        labelHidden
                                        options={[
                                            { label: `is less than`, value: "max_products" },
                                            { label: `is equal to`, value: "exact_products" },
                                            { label: `is greater than`, value: "min_products" },
                                        ]}
                                        onChange={(val) => handleEditSubStep(cIdx, "subStep_condition_type", val)}
                                        value={subStep.subStep_condition_type}
                                    />
                                    <FormTextField
                                        name={`steps.${pIdx}.subSteps.${cIdx}.product_count_limit`}
                                        label=""
                                        labelHidden
                                        type="number"
                                        suffix={
                                            subStep.subStep_condition_basis == "bundle_total"
                                                ? getNumberFieldSuffix("fixed", money_format).suffix
                                                : ""
                                        }
                                        value={subStep.product_count_limit as unknown as string}
                                        onChange={(val) => {
                                            const regex = /^(\d+(\.\d{0,2})?)?$/;
                                            setStepErrors((ps) => ({
                                                ...ps,
                                                product_count_limit: validateFontSize(
                                                    val,
                                                    subStep.subStep_condition_basis == "bundle_total"
                                                        ? "Amount"
                                                        : "Items count",
                                                    { min: 0, max: 10000000 },
                                                ),
                                            }));
                                            if (regex.test(val)) {
                                                handleEditSubStep(cIdx, "product_count_limit", val);
                                            }
                                        }}
                                        error={stepErrors?.product_count_limit}
                                    />
                                </div>
                                <div>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        <div
                                            style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: 13 }}
                                        >
                                            Tooltip Message
                                            <Tooltip
                                                width="wide"
                                                content="This message will appear when hovering over the 'Next' button, as long as the required condition is not met."
                                            >
                                                <Icon source={InfoIcon} tone="subdued" />
                                            </Tooltip>
                                        </div>
                                        <Tooltip
                                            width="wide"
                                            content={
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "flex-start",
                                                        justifyContent: "flex-start",
                                                    }}
                                                >
                                                    <span>{`{category_label} shows the label of this step`}</span>
                                                    {subStep.subStep_condition_basis == "items_count" && (
                                                        <span>{`{productCount} shows the number of product(s) the user is allowed to select in this step`}</span>
                                                    )}
                                                    {subStep.subStep_condition_basis == "bundle_total" && (
                                                        <span>{`{amount} shows the bundle total required in the step `}</span>
                                                    )}
                                                </span>
                                            }
                                        >
                                            <PiBracketsCurlyBold style={{ cursor: "pointer" }} />
                                        </Tooltip>
                                    </div>
                                    <FormTextField
                                        name={`steps.${pIdx}.subSteps.${cIdx}.step_error_msg`}
                                        label=""
                                        labelHidden
                                        placeholder="Enter tooltip message"
                                        value={subStep.step_error_msg}
                                        onChange={(val) => handleEditSubStep(cIdx, "step_error_msg", val)}
                                        maxLength={80}
                                        showCharacterCount
                                    />
                                </div>
                            </div>
                        )}
                        {/* Steps Conditions Ends */}
                    </FormLayout>
                </div>
            </Collapsible>
        </>
    );
};

const BBSubStep = ({
    subStep,
    cIdx,
    pIdx,
    handleEditSubStep = () => {},
    submit = () => {},
    expandSubStep,
    setExpandSubStep,
}: {
    subStep: SubStepType;
    cIdx: number;
    pIdx: number;
    handleEditSubStep: <K extends keyof SubStepType>(idx: number, key: K | "delete", value?: any) => void;
    submit: any;
    setExpandSubStep: Dispatch<SetStateAction<number>>;
    expandSubStep: number;
    money_format: string;
}) => {
    const [stepErrors, setStepErrors] = useState<{
        step_label: string;
        products: string;
        collections: string;
        product_count_limit: string;
    }>({
        step_label: "Label Has error",
        products: "Empty products",
        collections: "Empty collection",
        product_count_limit: "Empty product count",
    });
    const [hasInteracted, setHasInteracted] = useState(false);

    const { formState } = useFormContext();
    const subStepFieldErrors = (formState.errors as any)?.steps?.[pIdx]?.subSteps?.[cIdx] as
        | Record<string, any>
        | undefined;
    const hasSubStepFieldError = !!subStepFieldErrors;

    useEffect(() => {
        if (hasInteracted) {
            setStepErrors((ps) => ({
                ...ps,
                step_label: nonEmptyOverride(subStep.step_label, "Category Label is required"),
                product_count_limit: validateFontSize(
                    subStep.product_count_limit,
                    subStep.subStep_condition_basis == "bundle_total" ? "Amount" : "Items count",
                    { min: 0, max: 10000000 },
                ),
            }));
        } else {
            setStepErrors((ps) => ({ ...ps, step_label: "", product_count_limit: "" }));
        }
    }, [hasInteracted]);

    useEffect(() => {
        if (hasSubStepFieldError) setHasInteracted(true);
    }, [hasSubStepFieldError, cIdx, pIdx]);

    return (
        <>
            <div
                key={pIdx + cIdx + 10}
                onClick={() => setExpandSubStep((ps) => (ps == cIdx ? -1 : cIdx))}
                className={styles.customCardLabelWrapper}
                style={{
                    marginBottom: "5px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#ffffff",
                    padding: "16px",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                    <Icon source={DiscountIcon} />
                    <Label id="review_step">{`Category ${cIdx + 1}`}</Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandSubStep == cIdx ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor="pointer" />
                    </div>
                </div>
                <div className={styles.stepDragIconWrapper}>
                    <span
                        className={styles.stepDragIcon}
                        style={{ color: "#9b9b9b" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditSubStep(cIdx, "delete");
                        }}
                    >
                        <Icon source={DeleteIcon} tone="critical" />
                    </span>
                </div>
            </div>
            <Collapsible
                open={expandSubStep == cIdx}
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
                id="basic-collapsible"
            >
                <div style={{ padding: "0 16px 16px 16px" }}>
                    <FormLayout>
                        <div className={`${styles.formRow} ${styles.row4}`} style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <FormTextField
                                name={`steps.${pIdx}.subSteps.${cIdx}.step_label`}
                                label="Category Label"
                                placeholder="Category 1"
                                value={subStep.step_label}
                                onChange={(val) => {
                                    setStepErrors((ps) => ({
                                        ...ps,
                                        step_label: nonEmptyOverride(val, "Category Label is required"),
                                    }));
                                    if (!hasInteracted) setHasInteracted(true);
                                    handleEditSubStep(cIdx, "step_label", val);
                                }}
                                onBlur={() => {
                                    if (!hasInteracted) setHasInteracted(true);
                                }}
                                maxLength={20}
                                showCharacterCount
                                error={stepErrors?.step_label}
                            />
                        </div>
                        <div className={`${styles.formRow} ${styles.row2}`}>
                            <div>
                                <div style={{ display: "flex", flexDirection: "column", marginBottom: 5 }}>
                                    <FormSelect
                                        name={`steps.${pIdx}.subSteps.${cIdx}.product_selection`}
                                        label="Choose Products"
                                        options={[
                                            { label: "Specific Products", value: "specific" },
                                            { label: "Collection", value: "collection" },
                                        ]}
                                        onChange={(val) => handleEditSubStep(cIdx, "product_selection", val)}
                                        value={subStep?.product_selection}
                                    />
                                </div>
                                {subStep.product_selection == "specific" && (
                                    <div style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}>
                                        <RHFRenderSelectedProduct
                                            bundleLevel="product"
                                            bundleType={BundleTypes.BUNDLE_BUILDER}
                                            resourcePickerQuery={buildResourcePickerQuery({ includeUnlisted: true })}
                                            updateKey={`steps.${pIdx}.subSteps.${cIdx}.products`}
                                            multiple={true}
                                            value={subStep.products}
                                            errorMessage={subStepFieldErrors?.products?.message}
                                            onChange={(value: ProductObj[]) => {
                                                handleEditSubStep(cIdx, "products", value);
                                                submit();
                                            }}
                                        />
                                    </div>
                                )}

                                {subStep.product_selection == "collection" && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            marginBottom: 10,
                                            width: "100%",
                                        }}
                                    >
                                        <RHFRenderSelectedCollections
                                            updateKey={`steps.${pIdx}.subSteps.${cIdx}.collections`}
                                            multiple={false}
                                            value={subStep.collections}
                                            errorMessage={subStepFieldErrors?.collections?.message}
                                            onChange={(value: ProductObj[]) => {
                                                handleEditSubStep(cIdx, "collections", value);
                                                submit();
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            {isLilSweetTreatStore && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    <FormCheckbox
                                        name={`steps.${pIdx}.subSteps.${cIdx}.allow_product_custom_text`}
                                        label="Allow Customers to add Personalized Text for products"
                                        checked={subStep.allow_product_custom_text}
                                        onChange={(val) => handleEditSubStep(cIdx, "allow_product_custom_text", val)}
                                    />
                                    {subStep.allow_product_custom_text && (
                                        <div
                                            style={{
                                                paddingLeft: "25px",
                                                maxWidth: "400px",
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 10,
                                            }}
                                        >
                                            <FormTextField
                                                name={`steps.${pIdx}.subSteps.${cIdx}.product_custom_text_label`}
                                                label="Custom Text Label"
                                                value={subStep.product_custom_text_label}
                                                onChange={(val) =>
                                                    handleEditSubStep(cIdx, "product_custom_text_label", val)
                                                }
                                                placeholder="e.g. Add your name"
                                            />
                                            <FormTextField
                                                name={`steps.${pIdx}.subSteps.${cIdx}.product_custom_text_placeholder`}
                                                label="Custom Text Placeholder"
                                                value={subStep.product_custom_text_placeholder}
                                                onChange={(val) =>
                                                    handleEditSubStep(cIdx, "product_custom_text_placeholder", val)
                                                }
                                                placeholder="e.g. Enter your name here"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </FormLayout>
                </div>
            </Collapsible>
        </>
    );
};

export default BBSubStep;
